import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, BookOpen, Trash2, ArrowLeft, Edit3, Search, X } from "lucide-react";
import { pageTransition, fadeUp } from "@/lib/animations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";
import api from "@/lib/api";
import { toast } from "sonner";

const moods = ["reflective", "grateful", "hopeful", "nostalgic", "peaceful", "joyful", "melancholic"];

export default function JournalPage() {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", mood: "" });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  const loadEntries = async () => {
    try {
      const res = await api.get("/journal");
      setEntries(res.data.entries);
    } catch {
      toast.error("Failed to load journal");
    }
  };

  useEffect(() => { loadEntries(); }, []);

  useEffect(() => {
    if (entryId && entries.length) {
      const found = entries.find(e => e.entry_id === entryId);
      setSelectedEntry(found || null);
    }
  }, [entryId, entries]);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/journal", form);
      toast.success("Journal entry saved");
      setShowNew(false);
      setForm({ title: "", content: "", mood: "" });
      loadEntries();
    } catch {
      toast.error("Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/journal/${id}`);
      toast.success("Entry deleted");
      setSelectedEntry(null);
      loadEntries();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (selectedEntry) {
    return (
      <div className="p-6 md:p-12 max-w-3xl mx-auto" data-testid="journal-entry-view">
        <button
          onClick={() => { setSelectedEntry(null); navigate("/journal"); }}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8 transition-colors duration-200"
          data-testid="journal-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to journal
        </button>
        <div className="mb-4">
          {selectedEntry.mood && (
            <span className="text-xs bg-[#00FF88]/10 text-[#00FF88] px-3 py-1 rounded-full">
              {selectedEntry.mood}
            </span>
          )}
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
          {selectedEntry.title}
        </h1>
        <p className="text-sm text-slate-500 mb-8">
          {new Date(selectedEntry.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
        <div className="prose prose-invert max-w-none text-slate-300 text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: selectedEntry.content }} />
        <div className="mt-8 flex gap-3">
          <Button
            variant="outline"
            onClick={() => handleDelete(selectedEntry.entry_id)}
            data-testid="journal-delete-btn"
            className="border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/30 rounded-full"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      </div>
    );
  }

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-12 max-w-6xl mx-auto" data-testid="journal-page">
      <motion.div variants={fadeUp} className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Journal</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Your Journal
          </h1>
        </div>
        <Button
          onClick={() => setShowNew(true)}
          data-testid="journal-new-btn"
          className="bg-white text-black hover:bg-slate-200 rounded-full px-6 font-medium"
        >
          <Plus className="w-4 h-4 mr-2" /> New Entry
        </Button>
      </motion.div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Search journal entries..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!e.target.value.trim()) { setSearchResults(null); return; }
            const timeout = setTimeout(async () => {
              try {
                const res = await api.get("/search", { params: { q: e.target.value, content_type: "journal" } });
                setSearchResults(res.data.results);
              } catch { /* ignore */ }
            }, 400);
            return () => clearTimeout(timeout);
          }}
          data-testid="journal-search-input"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-xl h-12 pl-11"
        />
        {searchQuery && (
          <button onClick={() => { setSearchQuery(""); setSearchResults(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {(searchResults || entries).length === 0 ? (
        <div className="text-center py-24">
          <BookOpen className="w-12 h-12 text-slate-700 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">No journal entries yet</p>
          <p className="text-slate-600 text-sm mt-2">Start writing your thoughts and memories</p>
        </div>
      ) : (
        <div className="space-y-4">
          {(searchResults || entries).map((entry, i) => (
            <motion.div
              key={entry.entry_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => { setSelectedEntry(entry); navigate(`/journal/${entry.entry_id}`); }}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 cursor-pointer hover:border-white/15 transition-colors duration-300 group"
              data-testid={`journal-entry-${entry.entry_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium text-white truncate" style={{ fontFamily: 'Manrope' }}>
                      {entry.title}
                    </h3>
                    {entry.mood && (
                      <span className="text-xs bg-white/5 text-slate-400 px-2 py-0.5 rounded-full shrink-0">
                        {entry.mood}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2">{entry.content}</p>
                  <p className="text-xs text-slate-600 mt-3">
                    {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {entry.is_prompt_response && (
                      <span className="ml-2 text-[#00FF88]">Prompt Response</span>
                    )}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* New Entry Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#111]/95 backdrop-blur-2xl border-white/[0.1] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>New Journal Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              placeholder="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="journal-title-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
            />
            <RichTextEditor
              content={form.content}
              onChange={(html) => setForm({ ...form, content: html })}
              placeholder="Write your thoughts..."
            />
            <div>
              <p className="text-xs text-slate-500 mb-2">Mood (optional)</p>
              <div className="flex flex-wrap gap-2">
                {moods.map((m) => (
                  <button
                    key={m}
                    onClick={() => setForm({ ...form, mood: form.mood === m ? "" : m })}
                    data-testid={`mood-${m}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 ${
                      form.mood === m
                        ? "bg-[#00FF88]/20 text-[#00FF88] border border-[#00FF88]/30"
                        : "bg-white/5 text-slate-400 border border-white/[0.06] hover:border-white/15"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <Button
              onClick={handleCreate}
              disabled={saving}
              data-testid="journal-save-btn"
              className="w-full bg-white text-black hover:bg-slate-200 rounded-full h-12 font-medium"
            >
              {saving ? "Saving..." : "Save Entry"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
